import { useContext, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  IconButton,
  Tooltip,
  styled,
  Menu,
  MenuItem,
  Radio,
  Typography,
  useTheme
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import MenuTwoToneIcon from '@mui/icons-material/MenuTwoTone';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { SidebarContext } from 'src/contexts/SidebarContext';
import Logo from 'src/components/LogoHeader';
import { useSelector, useDispatch } from 'src/store';
import { setSelectedProducer } from 'src/slices/auth/authSlice';
import { PRODUCER_COLORS } from 'src/constants/producers';
import { isProducerSelectorLockedPath } from 'src/utils/producerSelectorLock';
import { rolesApp } from 'src/utils/rolesConstants';

// Mapa de rutas y los roles que tienen acceso a ellas
const getRequiredRolesForPath = (pathname: string): string[] => {
  // Home
  if (pathname.includes('/management/home')) {
    return [rolesApp.SUPERADMIN, rolesApp.PRODUCTOR];
  }
  // Events
  if (pathname.includes('/management/events')) {
    // Algunas sub-rutas de events permiten POS también
    if (pathname.includes('/sell') || pathname.includes('/list') || pathname.includes('/tickets/list')) {
      return [rolesApp.SUPERADMIN, rolesApp.PRODUCTOR, rolesApp.POS];
    }
    return [rolesApp.SUPERADMIN, rolesApp.PRODUCTOR, rolesApp.POS];
  }
  // Users
  if (pathname.includes('/management/users')) {
    return [rolesApp.ADMIN, rolesApp.SUPERADMIN];
  }
  // Roles
  if (pathname.includes('/management/roles')) {
    return [rolesApp.ADMIN, rolesApp.SUPERADMIN];
  }
  // Notifications
  if (pathname.includes('/management/notifications')) {
    return [rolesApp.PRODUCTOR, rolesApp.SUPERADMIN];
  }
  // News
  if (pathname.includes('/management/news')) {
    return [rolesApp.SUPERADMIN];
  }
  // Statistics (general)
  if (pathname.includes('/management/statistics')) {
    return [rolesApp.PRODUCTOR, rolesApp.SUPERADMIN];
  }
  // Tags
  if (pathname.includes('/management/tags')) {
    return [rolesApp.ADMIN, rolesApp.SUPERADMIN];
  }
  // Sell
  if (pathname.includes('/management/sell')) {
    return [rolesApp.POS, rolesApp.SUPERADMIN];
  }
  // Settings
  if (pathname.includes('/management/settings')) {
    return [rolesApp.SUPERADMIN, rolesApp.PRODUCTOR];
  }
  // Places
  if (pathname.includes('/management/places')) {
    return [rolesApp.SUPERADMIN, rolesApp.PRODUCTOR];
  }
  // Default: ningún rol tiene acceso, forzará redirección a /dashboard/management
  return [];
};

const HeaderWrapper = styled(Box)(
  ({ theme }) => `
    color: ${theme.header.textColor};
    padding: ${theme.spacing(0, 2)};
    margin-top: ${theme.spacing(3)};
    position: relative;
    justify-content: space-between;
    width: 100%;
  `
);

const ProducerButton = styled(Box)<{ clickable?: boolean }>(
  ({ theme, clickable = true }) => `
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
    padding: ${theme.spacing(1, 2)};
    border-radius: ${theme.shape.borderRadius}px;
    cursor: ${clickable ? 'pointer' : 'default'};
    transition: background-color 0.3s ease;
    
    ${clickable ? `
      &:hover {
        background-color: ${theme.palette.action.hover};
      }
    ` : ''}
  `
);

const ProducerCircle = styled(Box)<{ color: string }>(
  ({ color }) => `
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: ${color};
  `
);

function Header() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { toggleSidebar } = useContext(SidebarContext);
  const { selectedProducer, producers, isMultiproducer, roles } = useSelector((state) => state.auth);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Bloqueo del selector solo aplica cuando es multiproductora
  const isProducerSelectorLocked =
    isMultiproducer && isProducerSelectorLockedPath(location.pathname);

  const producerLockedTooltip = t(
    'You cannot change producer until you complete or cancel the current action.'
  );

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isMultiproducer && !isProducerSelectorLocked) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProducerChange = (producer: any) => {
    if (producer.id !== selectedProducer?.id) {
      dispatch(setSelectedProducer(producer));
      
      // Verificar si el usuario tiene permisos para la ruta actual con la nueva productora
      const newProducerId = producer.id.toString();
      const rolesList = Array.isArray(roles) ? roles : [];
      
      // Filtrar roles por la nueva productora seleccionada
      const rolesForNewProducer = rolesList.filter((role: any) => {
        const producerIds = Array.isArray(role.producerId) ? role.producerId : [];
        
        // Roles sin productoras asociadas se consideran globales
        if (producerIds.length === 0) {
          return true;
        }
        
        return producerIds.some((p: any) => {
          if (!p || p.id === undefined || p.id === null) {
            return false;
          }
          return p.id.toString() === newProducerId;
        });
      });
      
      // Obtener los IDs de los roles filtrados
      const roleIds = rolesForNewProducer.map((r: any) => r.id);
      
      // Obtener los roles requeridos para la ruta actual
      const requiredRoles = getRequiredRolesForPath(location.pathname);
      
      // Verificar si el usuario tiene alguno de los roles requeridos
      const hasPermission = roleIds.some((roleId: string) => requiredRoles.includes(roleId));
      
      // Si NO tiene permisos para la ruta actual, redirigir a /dashboard/management
      if (!hasPermission) {
        navigate('/dashboard/management');
      }
      // Si tiene permisos, se queda en la ruta actual (no navega)
    }
    handleClose();
  };

  const getProducerColor = (producerId: string) => {
    const index = producers.findIndex((p: any) => p.id === producerId);
    return PRODUCER_COLORS[index % PRODUCER_COLORS.length];
  };

  return (
    <HeaderWrapper display="flex" alignItems="center" flexDirection="row">
      <Box display="flex" alignItems="center">
        <Box component="span">
          <Tooltip arrow title="Menu">
            <IconButton color="primary" onClick={toggleSidebar}>
              <MenuTwoToneIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ paddingLeft: '10px' }}>
          <Logo />
        </Box>
      </Box>
      {isMultiproducer && selectedProducer && (
        <Box display="flex" alignItems="center">
          <Tooltip
            title={isProducerSelectorLocked ? producerLockedTooltip : ''}
            disableHoverListener={!isProducerSelectorLocked}
            placement="bottom"
            arrow
          >
            <span>
              <ProducerButton
                clickable={isMultiproducer && !isProducerSelectorLocked && producers.length > 1}
                onClick={handleClick}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: isProducerSelectorLocked
                      ? theme.palette.text.disabled
                      : theme.palette.text.primary
                  }}
                >
                  {selectedProducer.name}
                </Typography>
                <ProducerCircle color={getProducerColor(selectedProducer.id)} />
                {isMultiproducer && !isProducerSelectorLocked && producers.length > 1 && (
                  <KeyboardArrowDownIcon sx={{ color: theme.palette.text.primary }} />
                )}
              </ProducerButton>
            </span>
          </Tooltip>

          {isMultiproducer && !isProducerSelectorLocked && producers.length > 1 && (
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 250,
                  border: `2px solid ${theme.palette.primary.main}`,
                  borderRadius: 2
                }
              }}
            >
              {Array.isArray(producers) && producers.length > 0 && producers.map((producer: any, index: number) => (
                <MenuItem
                  key={`${producer.id}-${index}`}
                  onClick={() => handleProducerChange(producer)}
                  selected={producer.id === selectedProducer?.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    py: 1.5,
                    px: 2,
                    backgroundColor: producer.id === selectedProducer?.id
                      ? theme.palette.action.selected
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: producer.id === selectedProducer?.id
                        ? theme.palette.action.selected
                        : theme.palette.action.hover
                    }
                  }}
                >
                  <ProducerCircle color={PRODUCER_COLORS[index % PRODUCER_COLORS.length]} />
                  <Typography
                    variant="h5"
                    sx={{
                      flex: 1,
                      fontWeight: producer.id === selectedProducer?.id ? 600 : 400
                    }}
                  >
                    {producer.name}
                  </Typography>
                  <Radio
                    checked={producer.id === selectedProducer?.id}
                    sx={{
                      p: 0,
                      '&.Mui-checked': {
                        color: theme.palette.primary.main
                      }
                    }}
                  />
                </MenuItem>
              ))}
            </Menu>
          )}
        </Box>
      )}
    </HeaderWrapper>
  );
}

export default Header;
